/* eslint-disable no-undef */
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';

// Drop-in replacement for the (dead-on-React-19) redux-form v5 `reduxForm`
// higher-order component, backed by `react-hook-form`. It reproduces the exact
// v5 API the app's forms consume so each form only needs its import swapped:
//
//   @reduxForm({ form, fields: ['a', 'b'], initialState, enableReinitialize, validate },
//              mapStateToProps?, mapDispatchToProps?)
//
// Into the wrapped component it injects:
//   - `fields`: a map of `{ name, value, onChange, onBlur, touched, error, dirty }`
//     objects, spreadable onto inputs (`<Input {...fields.name} />`). `onChange`
//     accepts either a DOM event or a raw value (both are used in the codebase).
//   - `values`     – all current field values
//   - `handleSubmit(fn)` – returns an event handler that calls `fn(values)`
//   - `dirty`, `valid`, `invalid`, `submitting`
//   - `resetForm()`
//
// react-hook-form owns the underlying form state (via `useForm`/`watch`/
// `setValue`); this shim is only the v5-shaped facade over it.
//
// NOTE: forms are behavioral and cannot be verified without the running Rails
// app + browser — this preserves the v5 contract so behavior is unchanged, but
// each form still warrants runtime QA.

interface ReduxFormConfig {
  form?: string;
  fields?: string[];
  initialState?: Record<string, any>;
  initialValues?: Record<string, any>;
  enableReinitialize?: boolean;
  validate?: (values: Record<string, any>, props: any) => Record<string, any>;
  [key: string]: any;
}

// v5 field `onChange` was called with either a synthetic event or a raw value;
// unwrap events to their value/checked, pass raw values through untouched.
function extractValue(arg: any) {
  if (arg && typeof arg === 'object' && 'target' in arg && arg.target) {
    const target = arg.target;
    return target.type === 'checkbox' ? target.checked : target.value;
  }

  return arg;
}

// Hook form of the `reduxForm` HOC below, for function components. Returns the
// same v5-shaped facade the HOC injects as props (`fields`, `values`,
// `handleSubmit`, `dirty`, `valid`, `invalid`, `submitting`, `resetForm`).
// `runtimeProps` carries store-derived props (e.g. `initialValues`) when needed.
export function useReduxForm(config: ReduxFormConfig, runtimeProps: any = {}) {
  const {
    fields = [],
    initialState = {},
    initialValues: configInitialValues = {},
    validate,
    enableReinitialize,
  } = config;

  // initialValues may arrive via runtimeProps (mapStateToProps) or config.
  const defaults = useMemo(
    () => ({ ...initialState, ...configInitialValues, ...(runtimeProps.initialValues || {}) }),
    // Snapshot once for mount; reinitialize is handled explicitly below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const form = useForm({ defaultValues: defaults });
  const {
    watch, setValue, reset, getValues, formState,
  } = form;

  const values = watch();

  // redux-form's `enableReinitialize`: when the incoming initialValues change
  // (e.g. async data loaded into the store), reset the form to them.
  useEffect(() => {
    if (enableReinitialize && runtimeProps.initialValues) {
      reset({ ...initialState, ...configInitialValues, ...runtimeProps.initialValues });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(runtimeProps.initialValues)]);

  const errors = validate ? (validate(values, runtimeProps) || {}) : {};

  const fieldObjects: Record<string, any> = {};
  fields.forEach((name) => {
    const value = values[name];
    fieldObjects[name] = {
      name,
      value: value === undefined ? '' : value,
      // redux-form v5 exposed `checked` (a boolean) on checkbox/radio fields so
      // inputs could bind `checked={field.checked}`. react-hook-form only tracks
      // `value`, so mirror boolean values onto `checked` to restore that contract
      // (undefined for non-boolean fields, so text inputs are unaffected).
      checked: typeof value === 'boolean' ? value : undefined,
      onChange: (arg: any) => setValue(name, extractValue(arg), { shouldDirty: true }),
      onBlur: () => setValue(name, getValues(name), { shouldTouch: true }),
      touched: !!(formState.touchedFields as any)[name],
      error: errors[name],
      dirty: !!(formState.dirtyFields as any)[name],
    };
  });

  // v5 `handleSubmit(fn)` returned a submit handler; validation was left to
  // the component (it reads `valid`/`invalid`), so we don't gate here.
  const handleSubmit = (onSubmit: (vals: Record<string, any>) => any) => (e: any) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    return onSubmit(getValues());
  };

  const resetForm = () => reset(defaults);
  // redux-form v5 `initializeForm(values)`: make `values` the new pristine
  // baseline (react-hook-form `reset` sets defaults and clears dirty/touched).
  const initializeForm = (vals: Record<string, any>) => reset(vals);

  // redux-form v5 `untouch(...fields)` / `untouchAll()` cleared ONLY the
  // "touched" flag (e.g. after starting a save, so validation UI stops
  // showing) -- it did NOT clear dirty or change the pristine baseline.
  // Preserve that: `reset(getValues(), { keepDirty, keepDefaultValues,
  // keepValues, keepErrors })` re-seeds touched to false while leaving dirty
  // state, the defaults, and the current values intact. This matters because
  // callers (e.g. SingleSignOnDeconstructed.onConfirmUpdateAccount) call
  // untouchAll BEFORE the save is known to succeed; clearing dirty here would
  // make a failed save look pristine and drop it from the leave guard and
  // getChangedFieldValues retry payloads (PR #2281 review). The new pristine
  // baseline should be established only by a successful-save handler via
  // initializeForm. Fields are accepted for API compatibility but RHF resets
  // touched form-wide.
  const untouchOptions = {
    keepValues: true,
    keepDirty: true,
    keepDefaultValues: true,
    keepErrors: true,
  };
  const untouchAll = () => reset(getValues(), untouchOptions);
  const untouch = (..._fields: string[]) => reset(getValues(), untouchOptions);
  const valid = Object.keys(errors).length === 0;

  return {
    fields: fieldObjects,
    values,
    handleSubmit,
    dirty: formState.isDirty,
    valid,
    invalid: !valid,
    submitting: formState.isSubmitting,
    resetForm,
    initializeForm,
    untouch,
    untouchAll,
  };
}

export function reduxForm(
  config: ReduxFormConfig,
  mapStateToProps?: any,
  mapDispatchToProps?: any,
) {
  return function wrap(Wrapped: any) {
    function ReduxFormShim(props: any) {
      const formApi = useReduxForm(config, props);

      return <Wrapped {...props} {...formApi} />;
    }

    ReduxFormShim.displayName = `reduxForm(${Wrapped.displayName || Wrapped.name || 'Component'})`;

    // v5 accepted mapStateToProps/mapDispatchToProps as 2nd/3rd args (used here
    // to derive `initialValues` from the store); reproduce via react-redux connect.
    if (mapStateToProps || mapDispatchToProps) {
      return connect(mapStateToProps || null, mapDispatchToProps || null)(ReduxFormShim);
    }

    return ReduxFormShim;
  };
}

export default reduxForm;
